import { motion } from "framer-motion";
import {
  ArrowRight, Zap, Building2, Users, CreditCard, BarChart3,
  Bell, Shield, CheckCircle2, Sparkles, Target,
  TrendingUp, Award, Play,
  Calendar, Monitor, Heart, Key
} from "lucide-react";

/* ─── Design tokens matching existing components ─── */
const T = {
  navy:    "#0f172a",
  navy2:   "#1e293b",
  blue:    "#1d4ed8",
  blue2:   "#1e3a8a",
  sky:     "#93c5fd",
  slate:   "#64748b",
  border:  "#e2e8f0",
  bg:      "#f8fafc",
  white:   "#ffffff",
  green:   "#22c55e",
  greenBg: "#f0fdf4",
  greenBd: "#d1fae5",
  purp:    "#7c3aed",
  teal:    "#0891b2",
  amber:   "#d97706",
  pink:    "#db2777",
};

/* ─── Shared Badge ─── */
const Badge = ({ icon: Icon, label }) => (
  <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#eff6ff", border: "1px solid #bfdbfe", padding: "8px 18px", borderRadius: 100, marginBottom: 20 }}>
    {Icon && <Icon style={{ width: 14, height: 14, color: T.blue }} />}
    <span style={{ color: T.blue, fontSize: 13, fontWeight: 700, letterSpacing: "0.05em" }}>{label}</span>
  </div>
);

/* ══════════════════════════
   HERO
══════════════════════════ */
const Hero = () => (
  <section style={{ background: T.navy, padding: "100px 40px 80px", position: "relative", overflow: "hidden" }}>
    <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
    <div style={{ position: "absolute", top: "-30%", left: "50%", transform: "translateX(-50%)", width: "70%", height: "70%", background: "radial-gradient(ellipse, rgba(29,78,216,0.18) 0%, transparent 65%)", pointerEvents: "none" }} />

    <div style={{ maxWidth: 1280, margin: "0 auto", position: "relative" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }}>

        {/* Left */}
        <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(29,78,216,0.2)", border: "1px solid rgba(29,78,216,0.35)", padding: "7px 16px", borderRadius: 100, marginBottom: 24 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: T.green, display: "inline-block" }} />
            <span style={{ color: T.sky, fontSize: 13, fontWeight: 700, letterSpacing: "0.05em" }}>FLEX OFFICE MANAGEMENT PLATFORM</span>
          </div>
          <h1 style={{ fontSize: "clamp(2.4rem,4vw,3.8rem)", fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.08, color: "white", marginBottom: 24 }}>
            Run Your Coworking<br />Space on <span style={{ color: "#60a5fa" }}>Autopilot.</span>
          </h1>
          <p style={{ fontSize: 18, color: "rgba(255,255,255,0.6)", lineHeight: 1.75, marginBottom: 36, maxWidth: 500 }}>
            Krumago FlexOffice is the all-in-one management platform built for flex office operators. Automate bookings, billing, member management, and analytics — so you can focus on growing your space, not managing it.
          </p>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <motion.button whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.97 }}
              style={{ background: T.blue, color: "white", border: "none", padding: "15px 36px", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10, boxShadow: "0 0 40px rgba(29,78,216,0.4)" }}>
              Start Free Trial <ArrowRight style={{ width: 17, height: 17 }} />
            </motion.button>
            <motion.button whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.97 }}
              style={{ background: "rgba(255,255,255,0.06)", color: "white", border: "1px solid rgba(255,255,255,0.15)", padding: "15px 36px", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10 }}>
              <Play style={{ width: 15, height: 15 }} /> Watch Demo
            </motion.button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 20, marginTop: 40, paddingTop: 32, borderTop: "1px solid rgba(255,255,255,0.08)", flexWrap: "wrap" }}>
            {[
              { icon: CreditCard, label: "Automated Billing" },
              { icon: Calendar, label: "Smart Booking Engine" },
              { icon: BarChart3, label: "Live Occupancy Analytics" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(29,78,216,0.25)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon style={{ width: 14, height: 14, color: T.sky }} />
                </div>
                <span style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", fontWeight: 500 }}>{label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right — dashboard mockup */}
        <motion.div initial={{ opacity: 0, x: 36 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
          <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 24, padding: 24, backdropFilter: "blur(20px)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 20 }}>
              {["#ef4444","#f59e0b","#22c55e"].map(c => <div key={c} style={{ width: 11, height: 11, borderRadius: "50%", background: c }} />)}
              <div style={{ flex: 1, marginLeft: 10, height: 24, borderRadius: 6, background: "rgba(255,255,255,0.05)" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 16 }}>
              {[["—","Revenue / mo","#22c55e"],["—","Active Members","#60a5fa"],["—%","Occupancy","#f59e0b"]].map(([v,l,c]) => (
                <div key={l} style={{ background: "rgba(255,255,255,0.05)", borderRadius: 12, padding: "16px 14px" }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: c, marginBottom: 4 }}>{v}</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>{l}</div>
                </div>
              ))}
            </div>
            <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 14, padding: 16, marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: "0.08em", marginBottom: 12 }}>LIVE ROOM STATUS</div>
              {[["Conference Room","In Use","#22c55e"],["Open Desk Area","Available","#60a5fa"],["Meeting Pod","Booked","#f59e0b"],["Private Office","Available","#60a5fa"]].map(([name,status,color]) => (
                <div key={name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontSize: 13, color: "rgba(255,255,255,0.65)" }}>{name}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color, background: `${color}18`, padding: "3px 10px", borderRadius: 100 }}>{status}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={{ background: T.blue, color: "white", borderRadius: 10, padding: "11px 0", textAlign: "center", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>+ New Booking</div>
              <div style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)", borderRadius: 10, padding: "11px 0", textAlign: "center", fontSize: 13, fontWeight: 700, border: "1px solid rgba(255,255,255,0.1)", cursor: "pointer" }}>View Reports</div>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  </section>
);

/* ══════════════════════════
   MARQUEE
══════════════════════════ */
const Marquee = () => {
  const items = [
    { icon: Building2, label: "Space & Desk Management" },
    { icon: Calendar, label: "Instant Room Booking" },
    { icon: CreditCard, label: "Automated Recurring Billing" },
    { icon: Shield, label: "Access Control Integration" },
    { icon: BarChart3, label: "Live Occupancy Analytics" },
    { icon: Users, label: "Member Self-Service Portal" },
    { icon: Bell, label: "Smart Notifications" },
    { icon: TrendingUp, label: "Revenue Forecasting" },
  ];
  return (
    <div style={{ background: T.navy2, borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "18px 0", overflow: "hidden" }}>
      <motion.div animate={{ x: ["0%", "-50%"] }} transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
        style={{ display: "flex", gap: 56, whiteSpace: "nowrap" }}>
        {[...items, ...items].map((item, i) => {
          const Icon = item.icon;
          return (
            <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <Icon style={{ width: 15, height: 15, color: T.sky }} />
              <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, fontWeight: 600 }}>{item.label}</span>
              <span style={{ color: "rgba(255,255,255,0.15)", marginLeft: 20 }}>·</span>
            </div>
          );
        })}
      </motion.div>
    </div>
  );
};

/* ══════════════════════════
   MISSION / STORY
══════════════════════════ */
const Mission = () => (
  <section style={{ background: T.white, padding: "100px 40px" }}>
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }}>
        <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.65 }}>
          <div style={{ position: "relative" }}>
            <div style={{ background: "linear-gradient(135deg, #0f172a, #1e3a8a)", borderRadius: 28, padding: 40, boxShadow: "0 40px 80px rgba(15,23,42,0.22)" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.sky, letterSpacing: "0.1em", marginBottom: 24 }}>OPERATOR REVENUE DASHBOARD</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 80, marginBottom: 20 }}>
                {[38,55,42,70,50,80,65,88,60,92,75,100].map((h, i) => (
                  <motion.div key={i} initial={{ height: 0 }} whileInView={{ height: `${h}%` }} viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.5 }}
                    style={{ flex: 1, borderRadius: 4, background: i === 11 ? T.blue : "rgba(255,255,255,0.1)" }} />
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                <div>
                  <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 13 }}>Monthly Revenue</div>
                  <div style={{ color: "white", fontSize: 28, fontWeight: 900 }}>Your Numbers</div>
                  <div style={{ color: T.green, fontSize: 13, fontWeight: 600 }}>↑ Growing month-on-month</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 13 }}>Occupancy Rate</div>
                  <div style={{ color: "white", fontSize: 28, fontWeight: 900 }}>Live Data</div>
                </div>
              </div>
            </div>
            <motion.div animate={{ y: [0,-8,0] }} transition={{ duration: 4, repeat: Infinity }}
              style={{ position: "absolute", bottom: -22, right: -20, background: T.white, borderRadius: 18, padding: "16px 20px", boxShadow: "0 20px 50px rgba(0,0,0,0.12)", border: `1px solid ${T.border}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: T.greenBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CreditCard style={{ width: 18, height: 18, color: "#059669" }} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>Auto-Payment Collected</div>
                  <div style={{ fontSize: 12, color: T.slate }}>Recurring · $299/mo</div>
                </div>
              </div>
            </motion.div>
            <motion.div animate={{ y: [0,7,0] }} transition={{ duration: 5, repeat: Infinity, delay: 1 }}
              style={{ position: "absolute", top: -18, left: -16, background: T.blue, borderRadius: 16, padding: "12px 18px", boxShadow: "0 12px 36px rgba(29,78,216,0.4)" }}>
              <div style={{ color: "white", fontSize: 22, fontWeight: 900 }}>All-in-One</div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, fontWeight: 600 }}>Platform for Operators</div>
            </motion.div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.65 }}>
          <Badge icon={Sparkles} label="OUR STORY" />
          <h2 style={{ fontSize: "clamp(1.9rem,3vw,2.8rem)", fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.12, color: T.navy, marginBottom: 20 }}>
            We built what flex office owners actually needed.
          </h2>
          <p style={{ fontSize: 16, color: T.slate, lineHeight: 1.8, marginBottom: 18 }}>
            In 2021, our team spent months visiting coworking spaces around the world. We saw the same story everywhere: brilliant operators drowning in spreadsheets, chasing manual payments, and losing members to poor booking experiences.
          </p>
          <p style={{ fontSize: 16, color: T.slate, lineHeight: 1.8, marginBottom: 32 }}>
            Existing tools were too generic or too expensive. So we built Krumago FlexOffice — a platform designed from the ground up for the unique workflows of flexible workspace operators, from solo studios to multi-location chains.
          </p>
          {["Purpose-built for coworking, not adapted from it", "Replaces 6+ disconnected tools with one platform", "From 1 room to 100+ locations — scales with you", "Operators go live in under a day"].map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <CheckCircle2 style={{ width: 18, height: 18, color: T.green, flexShrink: 0 }} />
              <span style={{ fontSize: 15, fontWeight: 500, color: T.navy }}>{item}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);

/* ══════════════════════════
   PROBLEM / SOLUTION
══════════════════════════ */
const ProblemSolution = () => {
  const problems = ["Manual billing and chasing overdue payments","No real-time occupancy visibility","Booking conflicts and double reservations","Member data scattered across apps","Zero automated check-in or access control","No unified reporting or revenue insights"];
  const solutions = ["Automated recurring billing & smart invoicing","Live occupancy heatmaps across all spaces","Conflict-proof smart booking engine","Centralized member CRM with full history","QR & PIN check-in with usage tracking","Real-time analytics and revenue forecasting"];
  const benefits = [
    { icon: Building2, title: "Space Management", desc: "Manage desks, offices, meeting rooms, and common areas from one dashboard." },
    { icon: Users, title: "Member Experience", desc: "Self-service portal for bookings, invoices, and community engagement." },
    { icon: CreditCard, title: "Revenue Automation", desc: "Automated billing cycles, dunning flows, and plan upgrades without manual work." },
  ];

  return (
    <section style={{ background: T.bg, padding: "96px 40px", overflow: "hidden" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <Badge icon={Zap} label="THE FLEXOFFICE PROBLEM" />
          <h2 style={{ fontSize: 48, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.1, color: T.navy }}>
            Built specifically for <span style={{ color: T.blue }}>Flex Spaces</span>
          </h2>
          <p style={{ fontSize: 18, color: T.slate, maxWidth: 560, margin: "14px auto 0", lineHeight: 1.7 }}>
            Unlike generic business tools, Krumago FlexOffice is purpose-built for coworking operators.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <div style={{ background: T.white, border: "1px solid #fee2e2", borderRadius: 20, padding: 40 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
              <div style={{ width: 40, height: 40, background: "#fef2f2", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Building2 style={{ width: 20, height: 20, color: "#ef4444" }} />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 800, color: T.navy }}>Without Krumago FlexOffice</h3>
            </div>
            {problems.map((p, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span style={{ fontSize: 11, color: "#ef4444", fontWeight: 900 }}>✕</span>
                </div>
                <span style={{ color: T.slate, fontSize: 15 }}>{p}</span>
              </div>
            ))}
          </div>
          <div style={{ background: T.white, border: `1px solid ${T.greenBd}`, borderRadius: 20, padding: 40 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
              <div style={{ width: 40, height: 40, background: T.greenBg, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CheckCircle2 style={{ width: 20, height: 20, color: "#059669" }} />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 800, color: T.navy }}>With Krumago FlexOffice</h3>
            </div>
            {solutions.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <CheckCircle2 style={{ width: 18, height: 18, color: T.green, flexShrink: 0 }} />
                <span style={{ color: T.navy, fontSize: 15, fontWeight: 500 }}>{s}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20, marginTop: 24 }}>
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 16, padding: 28, textAlign: "center", cursor: "default", transition: "all 0.3s" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 20px 50px rgba(29,78,216,0.12)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}>
                <div style={{ width: 52, height: 52, background: "#eff6ff", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", color: T.blue }}>
                  <Icon style={{ width: 24, height: 24 }} />
                </div>
                <div style={{ fontWeight: 800, fontSize: 17, color: T.navy, marginBottom: 8 }}>{b.title}</div>
                <div style={{ color: T.slate, fontSize: 14, lineHeight: 1.6 }}>{b.desc}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ══════════════════════════
   FEATURES
══════════════════════════ */
const features = [
  { icon: Building2, title: "Space & Desk Management", desc: "Visualize your floor plan. Assign hot desks, private offices, and meeting rooms with real-time availability and capacity controls.", color: T.blue },
  { icon: Users, title: "Member Management CRM", desc: "Full member profiles with booking history, payment records, and activity logs — everything in one place.", color: T.purp },
  { icon: CreditCard, title: "Automated Billing", desc: "Set up recurring billing cycles, auto-generate invoices, and trigger payment reminders without manual effort.", color: "#059669" },
  { icon: BarChart3, title: "Occupancy Analytics", desc: "Live heatmaps, peak usage reports, and revenue forecasts help you maximize space utilization.", color: T.blue },
  { icon: Bell, title: "Smart Notifications", desc: "Booking confirmations, payment reminders, and renewal alerts — fully automated across email and SMS.", color: T.pink },
  { icon: Shield, title: "Access Control Sync", desc: "Auto-grant or revoke door access based on plan status. Integrates with leading access systems.", color: T.teal },
  { icon: Monitor, title: "Member Self-Service Portal", desc: "A branded portal where members manage their plan, book spaces, pay invoices, and connect.", color: T.purp },
  { icon: Target, title: "Smart Room Booking", desc: "Members self-book any space with instant confirmation. Conflict detection eliminates double reservations.", color: "#059669" },
];

const Features = () => (
  <section style={{ background: T.white, padding: "96px 40px" }}>
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 64 }}>
        <Badge icon={Zap} label="PLATFORM FEATURES" />
        <h2 style={{ fontSize: 48, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.1, color: T.navy }}>
          Everything to run your <span style={{ color: T.blue }}>Flex Space</span>
        </h2>
        <p style={{ fontSize: 18, color: T.slate, maxWidth: 560, margin: "14px auto 0", lineHeight: 1.7 }}>
          Purpose-built tools for coworking operators — from day-one setup to scaling across multiple locations.
        </p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 20 }}>
        {features.map((f, i) => {
          const Icon = f.icon;
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
              style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 18, padding: 28, cursor: "default", transition: "all 0.3s", position: "relative", overflow: "hidden" }}
              onMouseEnter={e => { e.currentTarget.style.background = T.white; e.currentTarget.style.borderColor = "#bfdbfe"; e.currentTarget.style.boxShadow = "0 12px 40px rgba(29,78,216,0.1)"; e.currentTarget.style.transform = "translateY(-4px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = T.bg; e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}>
              <div style={{ width: 52, height: 52, background: "#eff6ff", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", color: f.color, marginBottom: 18 }}>
                <Icon style={{ width: 24, height: 24 }} />
              </div>
              <div style={{ fontWeight: 800, fontSize: 16, color: T.navy, marginBottom: 10, lineHeight: 1.3 }}>{f.title}</div>
              <div style={{ color: T.slate, fontSize: 14, lineHeight: 1.6 }}>{f.desc}</div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ══════════════════════════
   HOW IT WORKS
══════════════════════════ */
const steps = [
  { step: "01", icon: Building2, color: T.blue, bg: "#eff6ff", title: "Set Up Your Space", desc: "Configure rooms, set capacity, define workspace types, and connect your payment gateway — all in minutes.", points: ["Map rooms & assign capacity", "Set plans & pricing tiers", "Connect payment gateway"] },
  { step: "02", icon: Users, color: T.purp, bg: "#f5f3ff", title: "Onboard Your Members", desc: "Invite members to your branded portal. They self-book desks, rooms, and manage their own accounts.", points: ["Send branded invite links", "Members self-service bookings", "Auto-billing activates on signup"] },
  { step: "03", icon: TrendingUp, color: "#059669", bg: T.greenBg, title: "Automate & Grow", desc: "Billing, access control, notifications, and reporting run on autopilot. You scale, we handle the ops.", points: ["Automated billing & reminders", "Live occupancy & revenue reports", "Scale to multiple locations"] },
];

const HowItWorks = () => (
  <section style={{ background: T.bg, padding: "96px 40px" }}>
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 64 }}>
        <Badge icon={TrendingUp} label="HOW IT WORKS" />
        <h2 style={{ fontSize: 48, fontWeight: 900, letterSpacing: "-0.02em", color: T.navy, lineHeight: 1.1 }}>
          Up and running in <span style={{ color: T.blue }}>3 simple steps</span>
        </h2>
        <p style={{ fontSize: 18, color: T.slate, maxWidth: 560, margin: "14px auto 0", lineHeight: 1.7 }}>
          From setup to full automation — get your flex space live on Krumago in less than a day.
        </p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24 }}>
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
              style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 20, padding: 36, position: "relative", transition: "all 0.3s" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-5px)"; e.currentTarget.style.boxShadow = "0 20px 50px rgba(29,78,216,0.1)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}>
              <div style={{ position: "absolute", top: -14, left: 28, width: 36, height: 36, background: T.white, border: `2px solid ${s.color}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 900, color: s.color }}>{s.step}</span>
              </div>
              <div style={{ width: 56, height: 56, background: s.bg, borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", color: s.color, marginBottom: 20, marginTop: 10 }}>
                <Icon style={{ width: 26, height: 26 }} />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 800, color: T.navy, marginBottom: 12 }}>{s.title}</h3>
              <p style={{ color: T.slate, fontSize: 15, lineHeight: 1.65, marginBottom: 22 }}>{s.desc}</p>
              {s.points.map((p, pi) => (
                <div key={pi} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <CheckCircle2 style={{ width: 15, height: 15, color: T.green, flexShrink: 0 }} />
                  <span style={{ color: "#374151", fontSize: 14, fontWeight: 500 }}>{p}</span>
                </div>
              ))}
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: s.color, borderRadius: "0 0 20px 20px" }} />
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ══════════════════════════
   PLATFORM PILLARS (replaces fake metrics)
══════════════════════════ */
const Pillars = () => {
  const pillars = [
    { icon: Building2, color: T.blue,  title: "Space Management",    desc: "Configure rooms, set capacity, manage floor plans, and control availability — all from one dashboard." },
    { icon: CreditCard, color: T.purp, title: "Billing Automation",  desc: "Recurring invoices, payment reminders, and dunning flows that run without any manual work." },
    { icon: Key,        color: "#059669", title: "Check-In & Access", desc: "QR and PIN-based check-in with automatic session tracking and access control sync." },
    { icon: BarChart3,  color: T.teal, title: "Analytics & Reports", desc: "Live occupancy heatmaps, revenue tracking, and utilization reports to help you make better decisions." },
  ];
  return (
    <section style={{ background: T.navy, padding: "80px 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 24 }}>
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                style={{ textAlign: "center", padding: "40px 24px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 20 }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: `${p.color}22`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                  <Icon style={{ width: 24, height: 24, color: p.color }} />
                </div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "white", marginBottom: 10 }}>{p.title}</div>
                <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 14, lineHeight: 1.65 }}>{p.desc}</div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ══════════════════════════
   TESTIMONIALS
══════════════════════════ */
 

/* ══════════════════════════
   VALUES
══════════════════════════ */
const values = [
  { icon: Target, color: T.blue, title: "Operator-First", desc: "Every feature is built for the real workflows of coworking operators — not adapted from generic tools." },
  { icon: Zap, color: T.amber, title: "Speed to Value", desc: "Operators go live in hours, not months. No complex setup. No IT team required." },
  { icon: Heart, color: T.pink, title: "Genuine Partnership", desc: "We treat every customer as a partner. Your feedback directly shapes our product roadmap." },
  { icon: Shield, color: "#059669", title: "Enterprise Reliability", desc: "99.98% uptime, SOC 2 compliant, and enterprise-grade security baked in from day one." },
];

const Values = () => (
  <section style={{ background: T.bg, padding: "96px 40px" }}>
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 56 }}>
        <Badge icon={Award} label="OUR VALUES" />
        <h2 style={{ fontSize: 48, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.1, color: T.navy }}>
          Why operators <span style={{ color: T.blue }}>choose us</span>
        </h2>
        <p style={{ fontSize: 18, color: T.slate, maxWidth: 500, margin: "14px auto 0", lineHeight: 1.7 }}>
          We're not just software — we're a team that genuinely cares about your space's success.
        </p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 20 }}>
        {values.map((v, i) => {
          const Icon = v.icon;
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 20, padding: 32, textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, background: `${v.color}12`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <Icon style={{ width: 26, height: 26, color: v.color }} />
              </div>
              <div style={{ fontWeight: 800, fontSize: 17, color: T.navy, marginBottom: 10 }}>{v.title}</div>
              <div style={{ color: T.slate, fontSize: 14, lineHeight: 1.65 }}>{v.desc}</div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ══════════════════════════
   CTA
══════════════════════════ */
const CTA = () => (
  <section style={{ background: T.white, padding: "96px 40px" }}>
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", borderRadius: 28, padding: "72px 56px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: "60%", height: "50%", background: "radial-gradient(ellipse, rgba(29,78,216,0.2), transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "relative" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(29,78,216,0.2)", border: "1px solid rgba(29,78,216,0.3)", color: T.sky, padding: "7px 18px", borderRadius: 100, fontSize: 12, fontWeight: 700, marginBottom: 24, letterSpacing: "0.06em" }}>
            <Sparkles style={{ width: 13, height: 13 }} /> BUILT FOR FLEX OFFICE OPERATORS
          </div>
          <h2 style={{ fontSize: 42, fontWeight: 900, letterSpacing: "-0.02em", color: "white", marginBottom: 18, lineHeight: 1.1 }}>
            Ready to automate your<br />flex office business?
          </h2>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 18, maxWidth: 520, margin: "0 auto 40px", lineHeight: 1.7 }}>
            Start a free trial today. No credit card required. Get your space live in under a day.
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <motion.button whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.97 }}
              style={{ background: T.blue, color: "white", border: "none", padding: "16px 40px", borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10, boxShadow: "0 0 40px rgba(29,78,216,0.45)" }}>
              Start Free Trial <ArrowRight style={{ width: 18, height: 18 }} />
            </motion.button>
            <motion.button whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.97 }}
              style={{ background: "rgba(255,255,255,0.07)", color: "white", border: "1px solid rgba(255,255,255,0.2)", padding: "16px 40px", borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: "pointer" }}>
              Book a Demo
            </motion.button>
          </div>
          <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 13, marginTop: 20 }}>No credit card · Cancel anytime · Setup in under a day</p>
        </div>
      </div>
    </div>
  </section>
);

/* ══════════════════════════
   ROOT
══════════════════════════ */
const FlexOfficeAbout = () => (
  <div>
    <Hero />
    <Marquee />
    <Mission />
    <ProblemSolution />
    <Features />
    <HowItWorks />
    <Pillars />
    {/* <Testimonials /> */}
    <Values />
    <CTA />
  </div>
);

export default FlexOfficeAbout;
